import React from 'react'
import {
  Typography,
  Backdrop,
  Fade,
  Box,
  Modal,
  Button,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Grid,
} from '@mui/material'
import useStyles from './styles'
import { isArray, isEmpty } from 'lodash'
import { ArrowForward, Cancel, CreditCardOff } from '@mui/icons-material'

/**
 * ConfirmDialog React Component.
 *
 * @component
 * @param {Object} props - React component props.
 * @param {boolean} [props.visible=false] - Visibility state of the confirm dialog.
 * @param {Function} [props.handleModal=() => null] - Function to handle modal visibility changes.
 * @param {string} [props.title=""] - Title of the confirm dialog.
 * @param {boolean} [props.btnLoad=false] - Loading state for the confirm button.
 * @param {string} [props.subTitle=""] - Subtitle or additional description for the confirm dialog.
 * @param {string} [props.linearColorsName] - Name of linear gradient colors for the confirm button.
 * @param {boolean} [props.isGradientButton] - Flag indicating whether the confirm button has a gradient.
 * @param {string} [props.paymentGatwayFee] - Payment gateway fee information.
 * @param {Object} [props.paymentData] - Data related to the payment.
 * @param {number} [props.finalAmount] - Final amount to be confirmed.
 * @param {Object} [props.chargeData] - Data related to the charge or payment process.
 * @param {string} [props.changeBtnText] - Text to change the confirm button's label.
 * @param {boolean} [props.noCharge] - Flag indicating whether there is no charge.
 * @returns {JSX.Element} ConfirmDialog React component.
 */
function ConfirmDialog(props) {
  const {
    visible = false,
    handleModal = () => null,
    title = '',
    btnLoad = false,
    subTitle = '',
    linearColorsName,
    isGradientButton,
    paymentGatwayFee,
    paymentData,
    finalAmount,
    chargeData,
    changeBtnText,
    noCharge,
    primaryColor,
    currency_symbol,
    tabSelected = {},
    isRemoveCard = false,
  } = props
  const styles = useStyles()
  const theme = useTheme()
  const sm = useMediaQuery(theme.breakpoints.down('sm'))
  const isAchPayment =
    !isEmpty(tabSelected) && tabSelected?.payment_method_id === 8
  const isUpdate = paymentData?.updateSubscription && !paymentData?.payThroughRecurrence
  const showAmountInBtn = !isUpdate

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: sm ? 320 : 500,
    bgcolor: 'background.paper',
    borderRadius: 1,
    boxShadow: 24,
    p: sm ? 2 : 3,
  }
  const renderAchDescription = () => {
    return (
      <Grid container>
        <Typography className={styles.achDescription} color={'#3F3F3F'}>
          Bank transfers may take up to 5 business days to complete. A
          verification code will be sent to your registered account — you'll
          need to enter it to finalise the transaction.
        </Typography>
        <Typography className={styles.achDescription} color={'#3F3F3F'}>
          Follow the on-screen instructions, and if you need any clarifications
          or have any problems, contact support by phone or email.
        </Typography>
        <Typography className={[styles.achDescription]} color='grey'>
          Credit cards and other payment methods are processed instantly.
        </Typography>
      </Grid>
    )
  }

  if (isRemoveCard) {
    return (
      <Modal
        open={visible}
        closeAfterTransition
        disableAutoFocus
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300 }}
      >
        <Fade in={visible}>
          <Box style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: sm ? 300 : 380,
            backgroundColor: '#fff',
            borderRadius: 16,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            overflow: 'hidden',
          }}>
            {/* Top section */}
            <div style={{ padding: '32px 28px 20px', textAlign: 'center' }}>
              {/* Icon */}
              <div style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ffebee, #ffcdd2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <CreditCardOff style={{ fontSize: 28, color: '#e53935' }} />
              </div>

              <Typography style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>
                Remove Card
              </Typography>
              <Typography style={{ fontSize: 13, color: '#8a96a8', lineHeight: 1.6 }}>
                This card will be permanently removed from your saved payment methods.
              </Typography>
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: '#f0f2f5' }} />

            {/* Buttons */}
            <div style={{ display: 'flex', padding: '12px 16px', gap: 10 }}>
              <div
                style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '11px 0',
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#5a6a7e',
                  cursor: btnLoad ? 'not-allowed' : 'pointer',
                  transition: 'background 0.15s',
                }}
                onClick={() => { if (!btnLoad && handleModal) handleModal() }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8f9fb'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                Cancel
              </div>
              <div
                style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '11px 0',
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#e53935',
                  cursor: btnLoad ? 'not-allowed' : 'pointer',
                  transition: 'background 0.15s',
                }}
                onClick={() => { if (!btnLoad && handleModal) handleModal(true) }}
                onMouseEnter={e => e.currentTarget.style.background = '#fff8f7'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {btnLoad ? <CircularProgress size={20} style={{ color: '#e53935' }} /> : 'Remove'}
              </div>
            </div>
          </Box>
        </Fade>
      </Modal>
    )
  }

  return (
    <Modal
      open={visible}
      closeAfterTransition
      disableAutoFocus
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={visible}>
        <Box sx={style}>
          <Grid
            container
            style={{
              justifyContent: 'space-between',
            }}
          >
            <Typography
              className={[
                styles.modalTitle,
                isAchPayment && { marginBottom: 10 },
              ]}
            >
              {title || ''}
            </Typography>
            {changeBtnText && (
              <Cancel
                style={{
                  color: 'grey',
                  cursor: 'pointer',
                  marginTop: sm ? 6 : 8,
                  fontSize: sm ? 22 : 26,
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  if (handleModal) {
                    handleModal()
                  }
                }}
              />
            )}
          </Grid>
          {isAchPayment && renderAchDescription()}
          {subTitle !== '' && (
            <Grid container>
              <Typography
                variant='caption'
                style={{ marginBottom: 20, fontSize: 14 }}
              >
                <b>Note: </b> {subTitle || ''}
              </Typography>
            </Grid>
          )}
          <div
            className={styles.splitViewStyle}
            style={{ justifyContent: 'unset' }}
          >
            {!changeBtnText && (
              <div
                style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '12px 0',
                  borderRadius: 10,
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#5a6a7e',
                  cursor: btnLoad ? 'not-allowed' : 'pointer',
                  opacity: btnLoad ? 0.5 : 1,
                  marginRight: 8,
                  border: '1.5px solid #e0e5ec',
                }}
                onClick={() => {
                  if (!btnLoad && handleModal) {
                    handleModal()
                  }
                }}
              >
                No
              </div>
            )}
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                padding: '12px 20px',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                color: '#fff',
                background: isGradientButton
                  ? `linear-gradient(124deg, ${linearColorsName[0]}, ${linearColorsName[1]})`
                  : primaryColor,
                cursor: btnLoad ? 'not-allowed' : 'pointer',
                justifyContent: changeBtnText && showAmountInBtn ? 'space-between' : 'center',
              }}
              onClick={() => {
                if (!btnLoad && handleModal) {
                  handleModal(true)
                }
              }}
            >
              {btnLoad ? (
                <CircularProgress size={28} style={{ color: '#fff' }} />
              ) : (
                <>
                  <span>{changeBtnText || 'Yes'}</span>
                  {changeBtnText && showAmountInBtn && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 15 }}>
                      {currency_symbol}{Number(finalAmount)?.toFixed(2)}
                      <ArrowForward style={{ fontSize: 18 }} />
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </Box>
      </Fade>
    </Modal>
  )
}

export default ConfirmDialog
